import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

const spinKeyframes = keyframes`
  to { transform: rotate(360deg); }
`;

export const MessageFilesWrap = styled.div`
  label: MessageFilesWrap;

  &[data-variant="bubble"] {
    margin: 0 0 0.6em;
  }

  &[data-variant="card"] {
    margin: 0.5rem 0 0;
  }
`;

export const MessageFilesGrid = styled.div`
  label: MessageFilesGrid;
  display: grid;
  gap: 6px;

  &[data-count="1"] {
    grid-template-columns: 1fr;
  }

  &[data-count="2"] {
    grid-template-columns: 1fr 1fr;
  }

  &[data-count="3"] {
    grid-template-columns: 1fr 1fr;

    > *:first-child {
      grid-column: 1 / -1;
    }
  }

  &[data-count="4"] {
    grid-template-columns: 1fr 1fr;
  }
`;

export const MessageFileCell = styled.div`
  label: MessageFileCell;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: var(--color-fog, #f5f5f5);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
  }

  /* All children fill the cell so only aspect-ratio drives the dimensions —
     no layout shift when the preview image finishes loading. */
  > * {
    position: absolute;
    inset: 0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* Anchor to the top so document headers (titles, first rows) stay
       visible instead of cropping to an unhelpful middle slice. */
    object-position: center top;
    display: block;
  }

  [data-count="1"] > & {
    aspect-ratio: 4 / 3;
  }

  [data-count="2"] > & {
    aspect-ratio: 1;
  }

  [data-count="3"] > &:first-child {
    aspect-ratio: 2 / 1;
  }

  [data-count="3"] > &:not(:first-child) {
    aspect-ratio: 1;
  }

  [data-count="4"] > & {
    aspect-ratio: 1;
  }

  [data-variant="card"] & {
    background: rgba(0, 0, 0, 0.04);
  }
`;

export const MessageFileCaption = styled.div`
  label: MessageFileCaption;
  display: flex;
  align-items: flex-end;
  padding: 0.55em 0.7em;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0.28) 40%,
    rgba(0, 0, 0, 0) 75%
  );
  pointer-events: none;
  z-index: 1;

  /* Width-constrained so CenterEllipsisText can measure overflow and
     middle-truncate the name (preserving the file extension at the end). */
  .caption-name {
    flex: 1;
    min-width: 0;
    color: #fff;
    font-size: 0.78rem;
    font-weight: 500;
    line-height: 1.25;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
  }
`;

export const MessageFileSpinner = styled.div`
  label: MessageFileSpinner;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 1.4rem;
    height: 1.4rem;
    border: 2px solid rgba(0, 0, 0, 0.15);
    border-top-color: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
    animation: ${spinKeyframes} 0.6s linear infinite;
  }
`;

export const MessageFileFallback = styled.div`
  label: MessageFileFallback;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.75rem;
  text-align: center;

  svg {
    width: 1.75rem;
    height: 1.75rem;
    opacity: 0.4;
  }

  /* Full width so CenterEllipsisText measures against the cell and
     middle-truncates rather than overflowing. */
  .fallback-name {
    width: 100%;
    text-align: center;
    font-size: 0.78rem;
    font-weight: 500;
    color: var(--color-charcoal, #212121);
  }

  .fallback-size {
    font-size: 0.7rem;
    color: var(--color-charcoal-60, rgba(33, 33, 33, 0.6));
  }
`;
